import React, { useState, useEffect, useCallback } from "react";
import {
  FaBolt,
  FaSearch,
  FaSyncAlt,
  FaCode,
  FaExclamationCircle,
  FaCheckCircle,
  FaTimesCircle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import PayloadModal from "./components/PayloadModal";
import "./TopmateBookings.css";

function TopmateWebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [eventType, setEventType] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [page, setPage] = useState(1);

  const [activePayload, setActivePayload] = useState(null);

  const fetchWebhookLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
        search: search.trim(),
        status,
        eventType,
        dateRange
      });

      const res = await fetchWithAuth(`/admin/topmate/webhook-logs?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setLogs(data.logs || []);
        setPagination(data.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
      } else {
        setError(data.message || "Failed to load webhook logs");
      }
    } catch (err) {
      setError("Network error fetching webhook logs");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, eventType, dateRange]);

  useEffect(() => {
    fetchWebhookLogs();
  }, [fetchWebhookLogs]);

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <FaBolt /> Webhook Synchronization
          </div>
          <h1>Webhook Logs</h1>
          <p>
            Audit log of all inbound Topmate webhook events, signature verification results, HTTP response status, and
            sanitized payload snapshots.
          </p>
        </div>

        <div className="topmate-header-actions">
          <button className="topmate-btn-secondary" onClick={fetchWebhookLogs} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="topmate-alert danger">
          <FaExclamationCircle /> <span>{error}</span>
        </div>
      )}

      {/* FILTERS */}
      <div className="filter-controls-bar">
        <div className="search-input-wrap">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="topmate-input"
            placeholder="Search event ID, event type, related ID, message..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="topmate-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Webhook Statuses</option>
          <option value="Success">Success (200)</option>
          <option value="Failed">Failed / Error</option>
          <option value="Ignored">Ignored</option>
        </select>

        <select
          className="topmate-select"
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Event Types</option>
          <option value="booking.created">booking.created</option>
          <option value="booking.rescheduled">booking.rescheduled</option>
          <option value="booking.cancelled">booking.cancelled</option>
          <option value="order.created">order.created</option>
        </select>

        <select
          className="topmate-select"
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Time (IST)</option>
          <option value="today">Today (IST)</option>
          <option value="7d">Last 7 Days (IST)</option>
          <option value="30d">Last 30 Days (IST)</option>
          <option value="90d">Last 90 Days (IST)</option>
          <option value="year">This Year (IST)</option>
        </select>
      </div>

      {/* TABLE */}
      <div className="topmate-card">
        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading webhook logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="topmate-state-box">
            <p>No webhook logs recorded yet.</p>
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Configure Topmate Outbound Webhooks with endpoint <code>https://portfolio-backend-aphl.onrender.com/api/webhooks/topmate</code>.
            </span>
          </div>
        ) : (
          <>
            <div className="topmate-table-wrapper">
              <table className="topmate-table">
                <thead>
                  <tr>
                    <th>Timestamp (IST)</th>
                    <th>Event ID</th>
                    <th>Event Type</th>
                    <th>Related Booking / Order ID</th>
                    <th>Portfolio ID</th>
                    <th>Processing Status</th>
                    <th>HTTP Status</th>
                    <th>Message / Details</th>
                    <th>Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ color: "#38bdf8" }}>{log.receivedAtIST}</td>
                      <td>
                        <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: "4px" }}>
                          {log.eventId || "—"}
                        </code>
                      </td>
                      <td>
                        <span style={{ fontSize: "0.75rem", background: "rgba(56, 189, 248, 0.12)", color: "#38bdf8", padding: "2px 8px", borderRadius: "6px" }}>
                          {log.eventType}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: "0.75rem" }}>{log.relatedId || "—"}</code>
                      </td>
                      <td>
                        {log.portfolioBookingId ? (
                          <code style={{ fontSize: "0.75rem", color: "#38bdf8" }}>{log.portfolioBookingId}</code>
                        ) : (
                          <span style={{ color: "#64748b" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-pill ${log.status === "Success" ? "completed" : "cancelled"}`}>
                          {log.status === "Success" ? (
                            <FaCheckCircle style={{ fontSize: "0.7rem" }} />
                          ) : (
                            <FaTimesCircle style={{ fontSize: "0.7rem" }} />
                          )}
                          {log.status}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            fontWeight: 600,
                            color: log.httpStatus >= 200 && log.httpStatus < 300 ? "#34d399" : "#f87171"
                          }}
                        >
                          {log.httpStatus}
                        </span>
                      </td>
                      <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <span title={log.errorDetails || log.message}>
                          {log.errorDetails || log.message || "—"}
                        </span>
                      </td>
                      <td>
                        {log.payloadSnapshot ? (
                          <button
                            className="btn-action"
                            onClick={() =>
                              setActivePayload({
                                title: `Payload: ${log.eventType} (${log.eventId || "snapshot"})`,
                                data: log.payloadSnapshot
                              })
                            }
                            title="View sanitized payload snapshot"
                          >
                            <FaCode /> View
                          </button>
                        ) : (
                          <span style={{ color: "#64748b" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="topmate-pagination">
              <div>
                Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} events
              </div>
              <div className="pagination-buttons">
                <button
                  className="btn-page"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.8rem", color: "#f8fafc" }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn-page"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* PAYLOAD MODAL */}
      {activePayload && (
        <PayloadModal
          title={activePayload.title}
          payload={activePayload.data}
          onClose={() => setActivePayload(null)}
        />
      )}
    </div>
  );
}

export default TopmateWebhookLogs;
